import React from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Col,
  Color,
  Display,
  Divider,
  FlexDirection,
  FontFamily,
  Grid,
  Heading2,
  Heading3,
  Join,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { RichText } from '../../components/RichText'
import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'

import type { TableFields } from './types'

export type TableProps = TableFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

type OrderedColProps = React.ComponentProps<typeof Col> & {
  mobileOrder: number
}

function BaseOrderedCol({ mobileOrder: _mobileOrder, ...props }: OrderedColProps) {
  return <Col {...props} />
}

// Citrus's `Col size` switches to the desktop span with `min-width: <bp>`, which is
// inclusive, so at exactly the breakpoint the desktop layout is already active. An
// inclusive `max-width: <bp>` here would overlap it by one pixel and re-apply the
// mobile order on top of the desktop columns — flipping the configured side at
// exactly 1024px, an iPad-landscape width. Subtracting a sub-pixel closes the gap
// without introducing a dead band between the two queries.
const OrderedCol = withCitrusTheme(BaseOrderedCol, (theme, props: OrderedColProps) => {
  const desktopBreakpoint = parseFloat(theme.tokens.breakpoints[2])
  const belowDesktop = `@media (max-width: ${desktopBreakpoint - 0.02}px)`
  return {
    [belowDesktop]: { order: props.mobileOrder },
  }
})

const StyledRichTextLink = withCitrusTheme(Link, (theme) => ({
  color: theme.color(Color.Secondary_400),
  textDecoration: 'underline',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  fontFamily: 'inherit',
}))

function renderRichTextLink({
  children,
  href,
  target,
  key,
}: {
  children: React.ReactNode
  href: string
  target?: string
  key: React.Key
}) {
  return (
    <StyledRichTextLink key={key} variant={LinkVariant.None} href={href} target={target}>
      {children}
    </StyledRichTextLink>
  )
}

const RichTextListItem = withCitrusTheme('li', (theme) => ({
  color: theme.color(Color.ColdGrey_600),
}))

function renderRichTextListItem({ children, key }: { children: React.ReactNode; key: React.Key }) {
  return <RichTextListItem key={key}>{children}</RichTextListItem>
}

function SectionHeading({ level, children }: { level: 'h2' | 'h3'; children: React.ReactNode }) {
  if (level === 'h2') {
    return <Heading2>{children}</Heading2>
  }
  return <Heading3>{children}</Heading3>
}

function getCellFlexBasis(columnCount: number): string {
  return `${100 / Math.max(columnCount, 1)}%`
}

export function Table({
  heading,
  heading_level,
  body,
  table_position,
  cta,
  columns,
  rows,
  onTriggerPromocode,
}: TableProps) {
  if (rows.length === 0) return null

  const columnCount = columns.length > 0 ? columns.length : rows[0]?.cells.length ?? 1
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)
  const tableOnLeft = table_position === 'left'
  const textMobileOrder = 1
  const tableMobileOrder = 2

  const TextColumn = (
    <OrderedCol
      size={[12, null, null, 6]}
      mobileOrder={textMobileOrder}
      paddingRight={tableOnLeft ? 0 : [0, null, null, 8]}
    >
      {heading && (
        <>
          <SectionHeading level={heading_level}>{heading}</SectionHeading>
          <Space size={3} />
        </>
      )}

      {body && (
        <Box paddingBottom={6}>
          <RichText
            content={body}
            paragraphGapSize={4}
            renderParagraph={({ children, key }) => (
              <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
                {children}
              </Body2>
            )}
            renderLink={renderRichTextLink}
            renderListItem={renderRichTextListItem}
          />
        </Box>
      )}

      {ctaLink && (
        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
          paddingBottom={[6, null, null, 0]}
        >
          <CtaLink cta={ctaLink} />
        </Box>
      )}
    </OrderedCol>
  )

  const TableColumn = (
    <OrderedCol
      size={[12, null, null, 6]}
      mobileOrder={tableMobileOrder}
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.Center}
    >
      <Box
        paddingRight={tableOnLeft ? [0, null, null, 8] : 0}
        maxWidth={[null, null, null, '600px']}
        width="100%"
      >
        <Box
          display={[Display.None, Display.Block]}
          style={{ borderRadius: '8px' }}
          boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
          bg={Color.White}
          width="100%"
        >
          {columns.length > 0 && (
            <Box bg={Color.ColdGrey_600} display={Display.Flex} paddingH={4} paddingV={3}>
              {columns.map((column, index) => (
                <Box key={index} flexBasis={getCellFlexBasis(columnCount)}>
                  <Body2 size={2} fontFamily={FontFamily.Bold} color={Color.White}>
                    {column.header}
                  </Body2>
                </Box>
              ))}
            </Box>
          )}

          <Join with={<Divider />}>
            {rows.map((row) => (
              <Box key={row.row_id} display={Display.Flex} paddingH={4} paddingV={3}>
                {row.cells.slice(0, columnCount).map((cell, index) => (
                  <Box
                    key={index}
                    flexBasis={getCellFlexBasis(columnCount)}
                    paddingRight={index < columnCount - 1 ? 3 : 0}
                  >
                    {cell.is_bold ? (
                      <Body2 size={2} fontFamily={FontFamily.Bold}>
                        {cell.value}
                      </Body2>
                    ) : (
                      <Body2 size={2}>{cell.value}</Body2>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Join>
        </Box>

        <Box display={[Display.Block, Display.None]}>
          <Join with={<Space size={4} />}>
            {rows.map((row) => (
              <Box
                key={row.row_id}
                style={{ borderRadius: '8px' }}
                boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
                bg={Color.White}
              >
                {row.cells[0] && (
                  <Box
                    bg={Color.ColdGrey_600}
                    style={{ borderRadius: '8px 8px 0 0' }}
                    paddingH={4}
                    paddingV={3}
                  >
                    <Body2 size={4} fontFamily={FontFamily.Bold} color={Color.White}>
                      {row.cells[0].value}
                    </Body2>
                  </Box>
                )}

                <Box paddingH={4} paddingV={3}>
                  {row.cells.slice(1, columnCount).map((cell, index) => {
                    const columnIndex = index + 1
                    const columnLabel = columns[columnIndex]?.header ?? ''

                    return (
                      <React.Fragment key={index}>
                        {index > 0 && <Divider />}
                        <Box
                          paddingH={2}
                          paddingV={2}
                          display={Display.Flex}
                          flexDirection={FlexDirection.Row}
                        >
                          {columnLabel && (
                            <>
                              <Box flexBasis="30%">
                                <Body2 fontFamily={FontFamily.Bold}>{columnLabel}</Body2>
                              </Box>
                              <Box flexBasis="70%">
                                <Body2 size={2}>{cell.value}</Body2>
                              </Box>
                            </>
                          )}
                          {!columnLabel && (
                            <Box flexBasis="100%">
                              {cell.is_bold ? (
                                <Body2 size={2} fontFamily={FontFamily.Bold}>
                                  {cell.value}
                                </Body2>
                              ) : (
                                <Body2 size={2}>{cell.value}</Body2>
                              )}
                            </Box>
                          )}
                        </Box>
                      </React.Fragment>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Join>
        </Box>
      </Box>
    </OrderedCol>
  )

  return (
    <Box bg={Color.NeutralGrey_50} paddingH={[8, 20]} paddingV={16} paddingBottom={6}>
      <Grid>
        {tableOnLeft ? TableColumn : TextColumn}
        {tableOnLeft ? TextColumn : TableColumn}
      </Grid>
    </Box>
  )
}
